import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { ArrowLeft, BookOpen, CheckCircle, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ChapterView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchProfile } = useAuth();
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchChapter();
  }, [id]);

  const fetchChapter = async () => {
    try {
      const response = await axios.get(`${API}/chapters/${id}`);
      setChapter(response.data);
    } catch (error) {
      console.error('Failed to fetch chapter', error);
      toast.error('Failed to load chapter');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await axios.post(`${API}/progress/complete`, {
        chapter_id: chapter.id,
        score: 100
      });
      toast.success(`Chapter completed! +${response.data.xp_gained} XP`);
      await fetchProfile();
      navigate(`/chapters/${chapter.level}`);
    } catch (error) {
      console.error('Failed to complete chapter', error);
      toast.error('Failed to mark as complete');
    } finally {
      setCompleting(false);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.split('v=')[1]?.split('&')[0] || url.split('/').pop();
    return `https://www.youtube.com/embed/${videoId}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!chapter) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-zinc-400">Chapter not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate(`/chapters/${chapter.level}`)}
          data-testid="back-to-chapters-button"
          className="mb-8 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Chapters
        </Button>

        <div className="mb-8" data-testid="chapter-header">
          <span className="text-xs font-mono uppercase tracking-widest text-primary/80">
            Chapter {chapter.order} • {chapter.level}
            {chapter.estimated_time && ` • ${chapter.estimated_time} min read`}
          </span>
          <h1 className="text-4xl md:text-5xl font-grotesk font-bold text-white mt-2 mb-4 tracking-tight">
            {chapter.title}
          </h1>
          <p className="text-lg text-zinc-400 leading-relaxed">{chapter.summary}</p>
        </div>

        {/* Video */}
        {chapter.youtube_url && (
          <div className="mb-8">
            <div className="mb-4 bg-zinc-900/30 border border-white/10 rounded-xl overflow-hidden" data-testid="youtube-video">
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src={getYouTubeEmbedUrl(chapter.youtube_url)}
                  title={chapter.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
            {chapter.youtube_channel && (
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/30 rounded-lg p-4 flex items-center space-x-3">
                <Youtube className="w-5 h-5 text-primary flex-shrink-0" />
                <div>
                  <p className="text-xs font-mono uppercase text-primary/80">Video Source</p>
                  <p className="text-white font-semibold">{chapter.youtube_channel}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-8 mb-8" data-testid="chapter-content">
          <div className="flex items-center space-x-2 mb-6">
            <BookOpen className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-grotesk font-semibold text-white">Content</h2>
          </div>
          <div className="text-zinc-300 leading-relaxed space-y-4">
            {chapter.content.split('\n\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>

        {/* Key Points */}
        {chapter.key_points && chapter.key_points.length > 0 && (
          <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-8 mb-8" data-testid="key-points">
            <h2 className="text-xl font-grotesk font-semibold text-white mb-6">Key Takeaways</h2>
            <div className="space-y-3">
              {chapter.key_points.map((point, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-zinc-300">{point}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Complete Button */}
        <div className="flex justify-center">
          <Button
            onClick={handleComplete}
            disabled={completing}
            data-testid="complete-chapter-button"
            className="bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide px-8 py-6 text-base h-auto rounded-sm"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            {completing ? 'Processing...' : 'Complete Chapter'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChapterView;
